
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Member {
  id: string;
  name: string;
  phone: string;
  school: string;
  grade: string;
  email: string;
  status: string;
  created_at: string;
}

interface Payment {
  id: string;
  member_id: string;
  amount: number;
  payment_month: string;
  status: string;
  payment_method: string;
  transaction_id: string;
  created_at: string;
}

export default function Payment() {
  const [member, setMember] = useState<Member | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const { data: memberData, error } = await supabase
        .from('members')
        .select('*')
        .eq('phone', phoneNumber)
        .single();

      if (error || !memberData) {
        setLoginError('등록된 회원 정보를 찾을 수 없습니다.');
        return;
      }

      setMember(memberData);
      await loadPayments(memberData.id);
    } catch (error) {
      setLoginError('로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (memberId: string) => {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('member_id', memberId)
      .order('payment_month', { ascending: false });

    if (!error && data) {
      setPayments(data);
    }
  };

  const handlePayment = async () => {
    if (!member) return;

    setPaymentLoading(true);
    
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      // 이번 달 결제 여부 확인
      const { data: existingPayment } = await supabase
        .from('payments')
        .select('*')
        .eq('member_id', member.id)
        .eq('payment_month', currentMonth)
        .single();

      if (existingPayment) {
        alert('이번 달 회비는 이미 결제되었습니다.');
        return;
      }

      // Stripe 결제 처리 (실제로는 Edge Function 호출)
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: 2990,
          currency: 'krw',
          member_id: member.id,
          payment_method: selectedMethod
        })
      });

      if (response.ok) {
        // 결제 성공 시 DB에 기록
        const { error } = await supabase
          .from('payments')
          .insert({
            member_id: member.id,
            amount: 2990,
            payment_month: currentMonth,
            status: 'completed',
            payment_method: selectedMethod,
            transaction_id: `txn_${Date.now()}`
          });

        if (!error) {
          alert('결제가 완료되었습니다!');
          await loadPayments(member.id);
        }
      } else {
        alert('결제 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      alert('결제 처리 중 오류가 발생했습니다.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const formatMonth = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return `${year}년 ${month}월`;
  };

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">회비 결제</h2>
            <p className="text-gray-600">전화번호로 로그인해주세요</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                전화번호
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="010-0000-0000"
              />
            </div>

            {loginError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  로그인 중...
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line mr-2"></i>
                  로그인
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 회원 정보 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">회비 결제</h1>
              <p className="text-gray-600">안녕하세요, {member.name}님!</p>
            </div>
            <button
              onClick={() => setMember(null)}
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-logout-circle-line text-xl"></i>
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">학교:</span>
              <span className="ml-2 font-medium">{member.school}</span>
            </div>
            <div>
              <span className="text-gray-500">학년:</span>
              <span className="ml-2 font-medium">{member.grade}</span>
            </div>
            <div>
              <span className="text-gray-500">연락처:</span>
              <span className="ml-2 font-medium">{member.phone}</span>
            </div>
            <div>
              <span className="text-gray-500">가입일:</span>
              <span className="ml-2 font-medium">{formatDate(member.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* 결제하기 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">이번 달 회비 결제</h2>
            
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">월 회비</span>
                <span className="text-2xl font-bold text-blue-600">2,990원</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {formatMonth(new Date().toISOString().slice(0, 7))} 회비
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                결제 방법
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="payment_method"
                    value="card"
                    checked={selectedMethod === 'card'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-3"
                  />
                  <i className="ri-bank-card-line text-xl mr-3 text-blue-600"></i>
                  <span>신용카드/체크카드</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="payment_method"
                    value="bank_transfer"
                    checked={selectedMethod === 'bank_transfer'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-3"
                  />
                  <i className="ri-bank-line text-xl mr-3 text-green-600"></i>
                  <span>계좌이체</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="payment_method"
                    value="mobile"
                    checked={selectedMethod === 'mobile'}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="mr-3"
                  />
                  <i className="ri-smartphone-line text-xl mr-3 text-purple-600"></i>
                  <span>모바일 결제</span>
                </label>
              </div>
            </div>

            <button
              onClick={handlePayment}
              disabled={paymentLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-4 rounded-xl font-semibold text-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {paymentLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  결제 처리 중...
                </>
              ) : (
                <>
                  <i className="ri-secure-payment-line mr-2"></i>
                  2,990원 결제하기
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 mt-4 text-center">
              안전한 결제를 위해 SSL 암호화를 사용합니다
            </p>
          </div>

          {/* 결제 내역 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">결제 내역</h2>
            
            {payments.length === 0 ? (
              <div className="text-center py-8">
                <i className="ri-file-list-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">결제 내역이 없습니다</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatMonth(payment.payment_month)} 회비
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatDate(payment.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {payment.amount.toLocaleString()}원
                        </p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : payment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {payment.status === 'completed' ? '완료' : 
                           payment.status === 'pending' ? '대기' : '실패'}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>결제방법: {
                        payment.payment_method === 'card' ? '카드' :
                        payment.payment_method === 'bank_transfer' ? '계좌이체' : '모바일'
                      }</span>
                      <span>거래번호: {payment.transaction_id}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
